import { Fragment, useState, type ReactNode } from "react";
import { Pencil, Plus } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampPuces, MentionObligatoire } from "@/composants/ui/champ";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import { FAMILLES, categorieDocument } from "@/lib/libelles";
import {
  api,
  type Famille,
  type PermissionCatalogue,
  type ProfilDetaille,
  type ReglageProfil,
  type TypeValidable,
} from "@/lib/api";

/**
 * Les profils, réglés par l'Administrateur sans le développeur (ADR-0035).
 *
 *     « Gérer des profils fonctionnels et leurs droits sans intervention du
 *       développeur. » — LAB, 12/09
 *
 * Un profil porte un libellé, une famille de périmètre, des permissions et, s'il
 * le faut, une restriction des types de documents qu'il valide à l'import OCR, et une
 * autre des documents remplis qu'il valide (ticket #115), sur le même moule.
 * Le catalogue des permissions reste du code : il se lit ici, il ne se règle pas —
 * sauf les clés de visibilité des sections de fiche (ticket #109, ADR-0067), que
 * l'Administrateur crée ici et donne aux profils comme une autre permission.
 *
 * L'écran ne recopie pas les garde-fous : la base refuse, et son message
 * s'affiche tel quel. Il dit seulement ce que le catalogue dit déjà — quelles
 * permissions sont ouvertes aux comptes externes — en toutes lettres (ADR-0024).
 * Une seule exception, demandée telle quelle : le profil Administrateur
 * s'affiche fixe, sans bouton pour un geste que la base refuserait toujours.
 */
const ADMINISTRATEUR = "ADMINISTRATEUR";
// Ticket #115 : la restriction des documents remplis n'a d'objet que sous elle.
const VALIDER_DOCUMENT = "VALIDER_DOCUMENT";

const OPTIONS_FAMILLE = (Object.keys(FAMILLES) as Famille[]).map((famille) => ({
  valeur: famille,
  libelle: FAMILLES[famille].libelle,
}));

export function PageProfils() {
  const profils = useChargement((signal) => api.profilsAdministres(signal), []);
  const catalogue = useChargement((signal) => api.catalogueDesPermissions(signal), []);
  const types = useChargement((signal) => api.typesOcr(signal), []);
  const validables = useChargement((signal) => api.typesValidables(signal), []);
  const [courants, setCourants] = useState<ProfilDetaille[] | null>(null);
  const [ajout, setAjout] = useState(false);
  const [enReglage, setEnReglage] = useState<string | null>(null);
  const [clesCreees, setClesCreees] = useState<PermissionCatalogue[]>([]);

  const erreur = profils.erreur ?? catalogue.erreur ?? types.erreur ?? validables.erreur;
  if (erreur) return <Erreur message={erreur} onReessayer={profils.recharger} />;
  const liste = courants ?? profils.donnees;
  if (!liste || !catalogue.donnees || !types.donnees || !validables.donnees)
    return <Chargement message="Chargement des profils…" />;

  // Rangé comme le serveur range le catalogue : par libellé, sans accents ni casse.
  const permissions = [...catalogue.donnees, ...clesCreees].sort((a, b) =>
    a.libelle.localeCompare(b.libelle, "fr", { sensitivity: "base" }),
  );
  const typesOcr = types.donnees;
  const typesValidables = validables.donnees;

  /** La réponse du serveur remplace la ligne : l'écran montre ce que la base a gardé. */
  function remplacer(profil: ProfilDetaille) {
    const autres = (liste ?? []).filter((p) => p.code !== profil.code);
    setCourants([...autres, profil].sort((a, b) => a.code.localeCompare(b.code)));
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-encre">Profils</h1>
          <p className="mt-1 text-sm text-encre-douce">
            Ce qu'un profil permet de faire et ce qu'il fait voir. Un profil ne se supprime pas ;
            on le retire aux comptes qui le portent.
          </p>
        </div>
        {!ajout ? (
          <Bouton onClick={() => setAjout(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            Nouveau profil
          </Bouton>
        ) : null}
      </div>

      {ajout ? (
        <Carte>
          <EnTeteCarte>
            <h2 className="text-sm font-semibold text-encre">Nouveau profil</h2>
          </EnTeteCarte>
          <CorpsCarte>
            <FormulaireProfil
              nom="Nouveau profil"
              permissions={permissions}
              typesOcr={typesOcr}
              typesValidables={typesValidables}
              onAnnuler={() => setAjout(false)}
              onValider={async (code, reglage) => {
                remplacer(await api.creerProfil(code, reglage));
                setAjout(false);
              }}
            />
          </CorpsCarte>
        </Carte>
      ) : null}

      <ClesVisibilite
        cles={permissions.filter((p) => p.cle_de_visibilite)}
        onCreee={(cle) => setClesCreees((avant) => [...avant, cle])}
      />

      {liste.map((profil) => (
        <article key={profil.code}>
          <Carte>
            <EnTeteCarte className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-col">
                <h2 className="text-sm font-semibold text-encre">{profil.libelle}</h2>
                <span className="font-mono text-xs break-all text-encre-douce">{profil.code}</span>
              </div>
              {profil.code !== ADMINISTRATEUR && enReglage !== profil.code ? (
                <Bouton
                  taille="compact"
                  variante="discret"
                  aria-label={`Régler ${profil.libelle}`}
                  onClick={() => setEnReglage(profil.code)}
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden />
                  Régler
                </Bouton>
              ) : null}
            </EnTeteCarte>
            <CorpsCarte>
              {enReglage === profil.code ? (
                <FormulaireProfil
                  nom={`Régler ${profil.libelle}`}
                  initial={profil}
                  permissions={permissions}
                  typesOcr={typesOcr}
                  typesValidables={typesValidables}
                  onAnnuler={() => setEnReglage(null)}
                  onValider={async (_code, reglage) => {
                    remplacer(await api.reglerProfil(profil.code, reglage));
                    setEnReglage(null);
                  }}
                />
              ) : (
                <ResumeProfil
                  profil={profil}
                  permissions={permissions}
                  typesValidables={typesValidables}
                />
              )}
            </CorpsCarte>
          </Carte>
        </article>
      ))}
    </div>
  );
}

/**
 * Ticket #109 : les clés de visibilité. Une section de modèle peut en porter une ; qui
 * ne la porte pas ne voit ni n'écrit la section. Une clé ne se supprime pas : des
 * versions publiées la citent.
 */
function ClesVisibilite({
  cles,
  onCreee,
}: {
  cles: PermissionCatalogue[];
  onCreee: (cle: PermissionCatalogue) => void;
}) {
  const [libelle, setLibelle] = useState("");
  const [externes, setExternes] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  return (
    <Carte>
      <EnTeteCarte>
        <h2 className="text-sm font-semibold text-encre">Clés de visibilité</h2>
      </EnTeteCarte>
      <CorpsCarte className="flex flex-col gap-3">
        <p className="text-sm text-encre-douce">
          Une section d'un modèle de document peut demander une clé : seuls les profils qui la
          portent la voient et la remplissent. Une clé se crée ici, puis se coche dans les profils.
        </p>
        {cles.length === 0 ? (
          <p className="text-sm text-encre-douce">Aucune clé pour l'instant.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm text-encre">
            {cles.map((cle) => (
              <li key={cle.code} className="break-words">
                {cle.libelle}
                {cle.ouverte_aux_externes ? (
                  <span className="text-encre-douce"> (ouverte aux comptes externes)</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <form
          className="flex flex-col gap-2"
          onSubmit={async (evenement) => {
            evenement.preventDefault();
            setErreur(null);
            setEnvoi(true);
            try {
              onCreee(await api.creerCleVisibilite(libelle, externes));
              setLibelle("");
              setExternes(false);
            } catch (cause) {
              setErreur(messageDe(cause));
            } finally {
              setEnvoi(false);
            }
          }}
        >
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1">
              <Champ
                libelle="Libellé de la clé"
                required
                maxLength={120}
                placeholder="Voir les notes de pose"
                value={libelle}
                onChange={(e) => setLibelle(e.target.value)}
              />
            </div>
            <Bouton type="submit" disabled={envoi}>
              <Plus className="h-4 w-4" aria-hidden />
              Créer la clé
            </Bouton>
          </div>
          <Case coche={externes} onChanger={setExternes}>
            Un compte externe peut la recevoir
          </Case>
          {erreur ? <Erreur message={erreur} /> : null}
        </form>
      </CorpsCarte>
    </Carte>
  );
}

/**
 * ADR-0047 : les permissions qui portent un plafond — celle des prix, et
 * depuis le ticket #50 celle des frais —, le champ du réglage qui le garde et
 * ce que l'écran en dit. La base le vérifie.
 */
const PLAFONNEES = {
  VALIDER_ECART_PRIX_FOURNISSEUR: {
    cle: "plafond_pourcentage",
    groupe: "Plafond des écarts de prix",
    objet:
      "Écart de prix fournisseur que le profil valide, par rapport au prix d'achat initial HT.",
    libelle: "Plafond d'écart de prix (%)",
    cleEuros: "plafond_euros",
    libelleEuros: "Plafond d'écart de prix sans prix initial (€)",
  },
  VALIDER_ECART_FRAIS_FOURNISSEUR: {
    cle: "plafond_frais_pourcentage",
    groupe: "Plafond des écarts de frais",
    objet:
      "Hausse de frais fournisseur que le profil valide au-delà de la tolérance, " +
      "par rapport au montant initial HT.",
    libelle: "Plafond d'écart de frais (%)",
    cleEuros: "plafond_frais_euros",
    libelleEuros: "Plafond d'écart de frais sans montant initial (€)",
  },
} as const;
type Plafonnee = keyof typeof PLAFONNEES;

function plafonnee(code: string): code is Plafonnee {
  return code in PLAFONNEES;
}

/**
 * « jusqu'à 12,5 % », ou « sans plafond » en toutes lettres (ADR-0024). Avec un
 * pourcentage, le plafond en euros des écarts sans prix initial (ticket #60) : sans
 * plafond en pourcentage il ne mesure plus rien et ne s'écrit pas.
 */
function libellePlafond(plafond: string | null, euros: string | null) {
  if (plafond === null) return "sans plafond";
  const pourcentage = `jusqu'à ${Number(plafond).toLocaleString("fr-FR")}\u00a0%`;
  const sansPrix =
    euros === null
      ? "aucun plafond en euros"
      : `jusqu'à ${Number(euros).toLocaleString("fr-FR")}\u00a0€`;
  return `${pourcentage}, sans prix initial : ${sansPrix}`;
}

function libellePorteurs(nombre: number) {
  if (nombre === 0) return "Aucun compte actif";
  return nombre === 1 ? "1 compte actif" : `${nombre} comptes actifs`;
}

function ResumeProfil({
  profil,
  permissions,
  typesValidables,
}: {
  profil: ProfilDetaille;
  permissions: PermissionCatalogue[];
  typesValidables: TypeValidable[];
}) {
  // Dans l'ordre du catalogue, qui est celui de la lecture.
  const portees = permissions.filter((p) => profil.permissions.includes(p.code));
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Etiquette ton="actif">Famille : {FAMILLES[profil.famille].libelle}</Etiquette>
        <span className="text-xs text-encre-douce">{libellePorteurs(profil.porteurs)}</span>
      </div>
      {profil.code === ADMINISTRATEUR ? (
        <p className="text-encre-douce">
          Profil fixe : toutes les permissions, et tout voir. Il ne se règle pas.
        </p>
      ) : (
        <>
          <div>
            <p className="text-xs text-encre-douce">Permissions</p>
            {portees.length === 0 ? (
              <p className="text-encre">Aucune permission</p>
            ) : (
              <ul className="mt-1 flex flex-wrap gap-1.5">
                {portees.map((permission) => (
                  // Un libellé long (« Regrouper les approvisionnements de plusieurs
                  // chargés d'affaires ») passe à la ligne plutôt que de déborder à 390 px.
                  <li key={permission.code} className="max-w-full">
                    <Etiquette className="whitespace-normal">
                      {permission.libelle}
                      {plafonnee(permission.code)
                        ? ` : ${libellePlafond(
                            profil[PLAFONNEES[permission.code].cle],
                            profil[PLAFONNEES[permission.code].cleEuros],
                          )}`
                        : null}
                    </Etiquette>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-xs text-encre-douce">Import OCR</p>
            <p className="text-encre">
              {profil.types_ocr.length === 0
                ? "Tous les types de documents"
                : profil.types_ocr.map(categorieDocument).join(", ")}
            </p>
          </div>
          {profil.permissions.includes(VALIDER_DOCUMENT) ? (
            <div>
              <p className="text-xs text-encre-douce">Documents remplis qu'il valide</p>
              <p className="text-encre">
                {profil.types_valides.length === 0
                  ? "Tous les types de documents remplis"
                  : profil.types_valides
                      .map((code) => libelleValidable(code, typesValidables))
                      .join(", ")}
              </p>
              {/* Restreint à des types qui ne demandent plus de validation, il ne valide
                  plus rien ; un type désactivé se valide encore sur les fiches en cours. */}
              {profil.types_valides.length > 0 &&
              !profil.types_valides.some((code) =>
                typesValidables.some((type) => type.code === code && type.demande_validation),
              ) ? (
                <p className="text-encre">
                  Aucun de ces types ne demande plus de validation : ce profil ne valide aucun
                  document rempli.
                </p>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/** Ticket #115 : un type se coche s'il est actif et demande une validation. */
function cochable(type: TypeValidable): boolean {
  return type.actif && type.demande_validation;
}

/** Le libellé d'un type coché, avec ce qui l'empêche d'être coché de nouveau. */
function libelleValidable(code: string, types: TypeValidable[]): string {
  const type = types.find((t) => t.code === code);
  if (!type) return code;
  if (!type.actif) return `${type.libelle} (désactivé)`;
  if (!type.demande_validation) return `${type.libelle} (ne demande plus de validation)`;
  return type.libelle;
}

/** Un profil à créer (code et réglage), ou à régler (le code ne change plus). */
function FormulaireProfil({
  nom,
  initial,
  permissions,
  typesOcr,
  typesValidables,
  onValider,
  onAnnuler,
}: {
  nom: string;
  initial?: ProfilDetaille;
  permissions: PermissionCatalogue[];
  typesOcr: string[];
  typesValidables: TypeValidable[];
  onValider: (code: string, reglage: ReglageProfil) => Promise<void>;
  onAnnuler: () => void;
}) {
  const [code, setCode] = useState(initial?.code ?? "");
  const [reglage, setReglage] = useState<ReglageProfil>({
    libelle: initial?.libelle ?? "",
    famille: initial?.famille ?? "AFFECTATIONS",
    permissions: initial?.permissions ?? [],
    types_ocr: initial?.types_ocr ?? [],
    types_valides: initial?.types_valides ?? [],
    plafond_pourcentage: initial?.plafond_pourcentage ?? null,
    plafond_frais_pourcentage: initial?.plafond_frais_pourcentage ?? null,
    plafond_euros: initial?.plafond_euros ?? null,
    plafond_frais_euros: initial?.plafond_frais_euros ?? null,
  });
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  // Les types qui se cochent, et ceux qu'un réglage déjà posé garde.
  const proposes = typesValidables.filter(
    (type) => cochable(type) || initial?.types_valides.includes(type.code),
  );

  function basculer(
    cle: "permissions" | "types_ocr" | "types_valides",
    valeur: string,
    coche: boolean,
  ) {
    const actuelles = reglage[cle].filter((v) => v !== valeur);
    setReglage({ ...reglage, [cle]: coche ? [...actuelles, valeur] : actuelles });
  }

  return (
    <form
      aria-label={nom}
      className="flex flex-col gap-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        setRefus(null);
        try {
          // Un plafond ne part qu'avec sa permission : décochée, elle l'emporte.
          const envoye = { ...reglage };
          for (const [permission, { cle, cleEuros }] of Object.entries(PLAFONNEES)) {
            if (!reglage.permissions.includes(permission)) {
              envoye[cle] = null;
              envoye[cleEuros] = null;
            }
          }
          // De même, la restriction des documents remplis ne part qu'avec sa permission.
          if (!reglage.permissions.includes(VALIDER_DOCUMENT)) envoye.types_valides = [];
          await onValider(code, envoye);
        } catch (cause) {
          setRefus(messageDe(cause));
        } finally {
          setEnvoi(false);
        }
      }}
    >
      <MentionObligatoire />
      <div className="grid gap-3 sm:grid-cols-2">
        <Champ
          libelle="Code"
          required
          disabled={initial !== undefined}
          maxLength={40}
          placeholder="METREUR"
          aide={
            initial
              ? "Le code ne change jamais : l'historique y fait référence."
              : "Majuscules, chiffres et tirets bas. Définitif une fois créé."
          }
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
        />
        <Champ
          libelle="Libellé"
          required
          maxLength={120}
          value={reglage.libelle}
          onChange={(e) => setReglage({ ...reglage, libelle: e.target.value })}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <ChampPuces
          libelle="Ce que le profil fait voir"
          required
          options={OPTIONS_FAMILLE}
          valeur={reglage.famille}
          onChanger={(famille) => setReglage({ ...reglage, famille: famille as Famille })}
        />
        <p className="text-xs text-encre-douce">{FAMILLES[reglage.famille].detail}</p>
      </div>

      <fieldset className="flex min-w-0 flex-col gap-2 border-0 p-0">
        <legend className="text-sm font-medium text-encre">Ce que le profil permet de faire</legend>
        <p className="text-xs text-encre-douce">
          Un compte externe ne reçoit que les permissions ouvertes aux comptes externes.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {permissions.map((permission) => {
            const regle = plafonnee(permission.code) ? PLAFONNEES[permission.code] : null;
            return (
              <Fragment key={permission.code}>
                <Case
                  coche={reglage.permissions.includes(permission.code)}
                  onChanger={(coche) => basculer("permissions", permission.code, coche)}
                >
                  {permission.libelle}
                  {permission.cle_de_visibilite ? (
                    <span className="text-encre-douce"> (clé de visibilité)</span>
                  ) : null}
                  {permission.ouverte_aux_externes ? (
                    <span className="text-encre-douce"> (ouverte aux comptes externes)</span>
                  ) : null}
                </Case>
                {/* Le plafond juste sous la case qu'il borne, sur toute la largeur. */}
                {regle && reglage.permissions.includes(permission.code) ? (
                  <Plafond
                    groupe={regle.groupe}
                    objet={regle.objet}
                    libelle={regle.libelle}
                    valeur={reglage[regle.cle]}
                    onChanger={(plafond) => setReglage({ ...reglage, [regle.cle]: plafond })}
                    libelleEuros={regle.libelleEuros}
                    euros={reglage[regle.cleEuros]}
                    onChangerEuros={(euros) => setReglage({ ...reglage, [regle.cleEuros]: euros })}
                  />
                ) : null}
              </Fragment>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="flex min-w-0 flex-col gap-2 border-0 p-0">
        <legend className="text-sm font-medium text-encre">
          Documents qu'il valide à l'import OCR
        </legend>
        <p className="text-xs text-encre-douce">Aucun coché : il les valide tous.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {typesOcr.map((type) => (
            <Case
              key={type}
              coche={reglage.types_ocr.includes(type)}
              onChanger={(coche) => basculer("types_ocr", type, coche)}
            >
              {categorieDocument(type)}
            </Case>
          ))}
        </div>
      </fieldset>

      {reglage.permissions.includes(VALIDER_DOCUMENT) ? (
        <fieldset className="flex min-w-0 flex-col gap-2 border-0 p-0">
          <legend className="text-sm font-medium text-encre">Documents remplis qu'il valide</legend>
          <p className="text-xs text-encre-douce">
            Aucun coché : il valide tous les types de documents remplis.
          </p>
          {proposes.length === 0 ? (
            <p className="text-sm text-encre">Aucun type de document ne demande de validation.</p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {proposes.map((type) => (
                <Case
                  key={type.code}
                  coche={reglage.types_valides.includes(type.code)}
                  onChanger={(coche) => basculer("types_valides", type.code, coche)}
                >
                  {libelleValidable(type.code, typesValidables)}
                </Case>
              ))}
            </div>
          )}
        </fieldset>
      ) : null}

      {refus ? <Erreur message={refus} /> : null}
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" variante="principal" disabled={envoi}>
          {initial ? "Enregistrer le réglage" : "Créer le profil"}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/**
 * Le plafond d'une validation d'écarts (ADR-0047), des prix ou des frais, à
 * côté de la permission qu'il borne. « Sans plafond » est une case, dite en
 * toutes lettres ; décochée, elle laisse saisir le pourcentage. À côté, le
 * plafond en euros des écarts sans prix initial (ticket #60) : vide, le profil ne
 * tranche pas ces écarts, sauf s'il est sans plafond en pourcentage.
 */
function Plafond({
  groupe,
  objet,
  libelle,
  valeur,
  onChanger,
  libelleEuros,
  euros,
  onChangerEuros,
}: {
  groupe: string;
  objet: string;
  libelle: string;
  valeur: string | null;
  onChanger: (plafond: string | null) => void;
  libelleEuros: string;
  euros: string | null;
  onChangerEuros: (euros: string | null) => void;
}) {
  const [saisie, setSaisie] = useState(valeur ?? "");
  return (
    <div
      role="group"
      aria-label={groupe}
      className="flex flex-col gap-2 rounded-[6px] border border-filet p-3 sm:col-span-2"
    >
      <p className="text-xs text-encre-douce">{objet}</p>
      <Case
        coche={valeur === null}
        onChanger={(sansPlafond) => onChanger(sansPlafond ? null : saisie)}
      >
        Sans plafond
      </Case>
      {valeur !== null ? (
        <Champ
          libelle={libelle}
          required
          type="number"
          inputMode="decimal"
          min="0.01"
          max="999.99"
          step="0.01"
          value={saisie}
          onChange={(e) => {
            setSaisie(e.target.value);
            onChanger(e.target.value);
          }}
        />
      ) : null}
      <Champ
        libelle={libelleEuros}
        aide="Un écart sans prix initial n'a pas de pourcentage : ce plafond, en euros, le mesure. Vide, le profil ne tranche pas ces écarts, sauf s'il est sans plafond en pourcentage."
        type="number"
        inputMode="decimal"
        min="0.01"
        max="9999999999.99"
        step="0.01"
        value={euros ?? ""}
        onChange={(e) => onChangerEuros(e.target.value === "" ? null : e.target.value)}
      />
    </div>
  );
}

/** Une case à cocher : la coche native dit l'état, pas la couleur (ADR-0024). */
function Case({
  coche,
  onChanger,
  children,
}: {
  coche: boolean;
  onChanger: (coche: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm text-encre">
      <input
        type="checkbox"
        checked={coche}
        onChange={(e) => onChanger(e.target.checked)}
        className="h-4 w-4 shrink-0 accent-laiton"
      />
      <span>{children}</span>
    </label>
  );
}
